/**
 * yt-referral.js — affiliate referral capture, site-wide (Lucas, 15 Sep
 * 2026 — partner programme Phase 2, docs/12-partners/02 §5.8 and 00-plan
 * "storefront"). Loaded from layout/theme.liquid on every page.
 *
 * What it does, and only with MARKETING consent from Shopify's Customer
 * Privacy API (the same gate as every marketing tag on the site):
 *
 *   1. Reads `ref` (the affiliate's referral code) and `clk` (a signed click
 *      id) from the page URL. A short link arrives with both; a long link
 *      someone pasted arrives with `ref` alone.
 *   2. Stores {ref, clk, code, at} in localStorage for 30 days — LAST CLICK
 *      WINS, so a later affiliate's link replaces an earlier one.
 *   3. When `ref` came without `clk`, asks the click beacon
 *      (POST /apps/yutrients/partner-click) for a signed id and stores it.
 *      A click id the visitor could type themselves would be worthless;
 *      the server signs it and later verifies it against its own row.
 *   4. Writes the stored values to the cart as attributes `_yt_ref` and
 *      `_yt_click` via /cart/update.js — when a cart exists on load, after
 *      every cart change the theme announces, and just before checkout —
 *      so orders/paid can attribute anything bought inside the window.
 *
 * THE DISCOUNT APPLIES ITSELF (founder 17 Sep 2026: "the discount code
 * should be auto applied in checkout"). The beacon now answers with the
 * affiliate's half of the pot as `{code, percent}`, which we keep beside
 * the ref for the same 30 days and apply ONCE by sending the visitor to
 * /discount/<CODE>?redirect=<the page they asked for>. Shopify sets the
 * code on their session and hands them straight back to the same URL.
 * "Once" is held in sessionStorage, so a reload cannot loop — and if
 * sessionStorage cannot be written at all, we would have no way of
 * knowing we had already been, so we do not go.
 *
 * A short link needs none of this: /l/<code> already sent the visitor
 * through /discount before they arrived, which is why it comes with `clk`
 * and never asks the beacon.
 *
 * Without consent it stores NOTHING and writes NOTHING; a visitor who
 * declines simply never earns anyone a commission. Consent is re-checked
 * on `visitorConsentCollected`, so accepting the banner after landing
 * still captures the referral that brought them here.
 *
 * It never touches a cart that already carries `_yt_plan`: those are
 * practitioner-plan carts (Phase 3), which set their own attribution.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'yt_ref';
  var APPLIED_KEY = 'yt_ref_discount'; // sessionStorage: the code applied this session
  var TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days, the click window
  var BEACON = '/apps/yutrients/partner-click';
  var REF_RE = /^[A-Za-z0-9_-]{2,40}$/;
  var CLK_RE = /^[0-9a-fA-F-]{36}\.[A-Za-z0-9_-]{20,100}$/;
  /* A Shopify discount code as we are willing to put it in a path. */
  var CODE_RE = /^[A-Za-z0-9._~-]{2,64}$/;
  var PLAN_ATTR = '_yt_plan';

  var syncing = false;
  var lastWritten = null;
  var leaving = false;

  function consentApi() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  /* Same helper as snippets/yt-head.liquid: the method when the API has it,
     the consent object otherwise. Anything unsure counts as "no". */
  function marketingAllowed() {
    var api = consentApi();
    if (!api) return false;
    try {
      if (typeof api.marketingAllowed === 'function') return !!api.marketingAllowed();
      var c = typeof api.currentVisitorConsent === 'function' && api.currentVisitorConsent();
      return !!(c && c.marketing === 'yes');
    } catch (e) {
      return false;
    }
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || typeof v !== 'object' || !REF_RE.test(String(v.ref || ''))) return null;
      if (!v.at || Date.now() - Number(v.at) > TTL_MS) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return {
        ref: String(v.ref),
        clk: CLK_RE.test(String(v.clk || '')) ? String(v.clk) : null,
        code: CODE_RE.test(String(v.code || '')) ? String(v.code) : null,
        at: Number(v.at),
      };
    } catch (e) {
      return null;
    }
  }

  function writeStored(v) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
    } catch (e) {
      /* storage blocked — nothing to persist, nothing to attribute */
    }
  }

  function clearStored() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function readUrl() {
    try {
      var u = new URL(window.location.href);
      var ref = u.searchParams.get('ref') || '';
      var clk = u.searchParams.get('clk') || '';
      var blend = u.searchParams.get('blend') || '';
      return {
        ref: REF_RE.test(ref) ? ref : null,
        clk: CLK_RE.test(clk) ? clk : null,
        blend: /^[0-9a-fA-F-]{36}$/.test(blend) ? blend : null,
      };
    } catch (e) {
      return { ref: null, clk: null, blend: null };
    }
  }

  /* Ask the formulator for a signed click id for a long link. A 404 means
     the code is unknown, blocked or not an affiliate: forget the ref. */
  function requestClick(ref, blend) {
    // Only ever called after marketingAllowed(); say so, the row records it.
    var body = { ref: ref, consented: true };
    if (blend) body.blend = blend;
    return fetch(BEACON, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        if (res.status === 404) return { drop: true };
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  /* Capture whatever the URL carries into storage (consented only). */
  function capture() {
    if (!marketingAllowed()) return Promise.resolve(null);
    var url = readUrl();
    if (url.ref) {
      // A repeat click on the SAME affiliate keeps the code we already
      // know; a different affiliate's link replaces everything.
      var prev = readStored();
      var entry = {
        ref: url.ref,
        clk: url.clk,
        code: prev && prev.ref === url.ref ? prev.code : null,
        at: Date.now(),
      };
      writeStored(entry); // last click wins
      if (!entry.clk) {
        return requestClick(url.ref, url.blend).then(function (out) {
          if (out && out.drop) {
            clearStored();
            return null;
          }
          var changed = false;
          if (out && CLK_RE.test(String(out.clk || ''))) {
            entry.clk = String(out.clk);
            changed = true;
          }
          // The affiliate's half of the pot, for the visitor to spend
          // without ever typing it (founder 17 Sep 2026).
          var code = out && out.discount && out.discount.code;
          if (CODE_RE.test(String(code || ''))) {
            entry.code = String(code);
            changed = true;
          }
          if (changed) {
            // Only if nobody has since replaced the entry.
            var current = readStored();
            if (current && current.ref === entry.ref && current.at === entry.at) writeStored(entry);
          }
          return readStored();
        });
      }
      return Promise.resolve(entry);
    }
    return Promise.resolve(readStored());
  }

  /* ── Applying the code, once (founder 17 Sep 2026) ─────────────────── */

  /* Pages we must never bounce off: a checkout in progress, and /discount
     itself — that is where this redirect is going. */
  function offLimits(pathname) {
    return /^\/(?:discount(?:\/|$)|checkouts?(?:\/|$)|\d+\/checkouts(?:\/|$))/.test(pathname);
  }

  /* "Have we been already?" — kept in sessionStorage so a reload does not
     loop. A storage we cannot READ means we could never tell, so it counts
     as "yes" and nothing happens. */
  function alreadyApplied(code) {
    try {
      return window.sessionStorage.getItem(APPLIED_KEY) === code;
    } catch (e) {
      return true;
    }
  }

  /* And a storage we cannot WRITE means the same: no memory, no redirect. */
  function markApplied(code) {
    try {
      window.sessionStorage.setItem(APPLIED_KEY, code);
      return window.sessionStorage.getItem(APPLIED_KEY) === code;
    } catch (e) {
      return false;
    }
  }

  /**
   * One navigation to /discount/<CODE>?redirect=<where they already are>,
   * so the code is on the session before they reach the basket. Only with
   * consent, only with a code, only once, and never over a fragment we
   * would destroy on the way (the practitioner plan link carries its token
   * in one).
   */
  function applyDiscount(stored) {
    if (leaving || !marketingAllowed()) return;
    var code = stored && CODE_RE.test(String(stored.code || '')) ? String(stored.code) : null;
    if (!code) return;
    var here;
    try {
      if (window.location.hash) return;
      if (offLimits(window.location.pathname)) return;
      here = window.location.pathname + window.location.search;
    } catch (e) {
      return;
    }
    if (alreadyApplied(code)) return;
    if (!markApplied(code)) return;
    leaving = true;
    // replace, not assign: Back should take them where they came from,
    // not back through the redirect.
    window.location.replace('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(here));
  }

  /* Write _yt_ref / _yt_click onto the cart. Skips plan carts, skips when
     nothing changed, skips an empty cart (no cart, no attributes). */
  function syncCart() {
    if (syncing) return Promise.resolve();
    if (!marketingAllowed()) return Promise.resolve();
    var stored = readStored();
    if (!stored) return Promise.resolve();
    var want = { _yt_ref: stored.ref, _yt_click: stored.clk || '' };
    var key = want._yt_ref + '|' + want._yt_click;
    if (key === lastWritten) return Promise.resolve();
    syncing = true;
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return null;
        var attrs = cart.attributes || {};
        if (attrs[PLAN_ATTR]) return null; // practitioner-plan cart: theirs, not ours
        if (attrs._yt_ref === want._yt_ref && (attrs._yt_click || '') === want._yt_click) {
          lastWritten = key;
          return null;
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: want }),
        }).then(function (res) {
          if (res.ok) lastWritten = key;
          return null;
        });
      })
      .catch(function () {
        return null;
      })
      .then(function () {
        syncing = false;
      });
  }

  function run() {
    capture().then(function (stored) {
      // The cart attributes first — they are what earns the commission,
      // and applying the code navigates away from this page.
      return syncCart().then(function () {
        applyDiscount(stored);
      });
    });
  }

  /* Cart changes the theme announces (Dawn's pubsub and our drawer), plus
     a short delay so the cart is settled before we read it. */
  function onCartChange() {
    window.setTimeout(syncCart, 300);
  }
  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    try {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, onCartChange);
    } catch (e) {
      /* pubsub not ready — the DOM events below still cover it */
    }
  }
  document.addEventListener('cart:updated', onCartChange);
  document.addEventListener('cart:refresh', onCartChange);
  document.addEventListener('yt:cart-updated', onCartChange);

  /* Just before checkout: any element that goes to /checkout, and the cart
     form's checkout submit. A synchronous write is not possible; the sync
     already ran on the last cart change, so this is belt and braces. */
  document.addEventListener(
    'click',
    function (ev) {
      var el = ev.target && ev.target.closest && ev.target.closest('a[href^="/checkout"], [name="checkout"], form[action^="/checkout"] button');
      if (el) syncCart();
    },
    true,
  );

  /* Consent may arrive after landing: run again when the banner is saved. */
  document.addEventListener('visitorConsentCollected', run);

  if (window.Shopify && window.Shopify.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
      if (!err) run();
    });
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
